import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ArtworkCard from "@/components/ArtworkCard";
import { Search, Loader2 } from "lucide-react";

const CATEGORIES = ["All", "Painting", "Photography", "Sculpture", "Digital", "Print", "Mixed Media"];
const RANGES = [
  { label: "Any price", min: null, max: null },
  { label: "Under €1,000", min: null, max: 1000 },
  { label: "€1,000 – €5,000", min: 1000, max: 5000 },
  { label: "€5,000 – €20,000", min: 5000, max: 20000 },
  { label: "€20,000+", min: 20000, max: null },
];

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [q, setQ] = useState("");
  const [range, setRange] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { category };
      if (q) params.q = q;
      const r = RANGES[range];
      if (r.min != null) params.min_price = r.min;
      if (r.max != null) params.max_price = r.max;
      const { data } = await api.get("/artworks", { params });
      setItems(data);
    } catch (e) { console.error("Failed to load artworks:", e); } finally { setLoading(false); }
  }, [category, q, range]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="overline-badge">The Collection</div>
        <h1 className="font-serif text-4xl sm:text-5xl mt-3">Gallery</h1>
        <p className="text-[var(--text-secondary)] mt-3 max-w-xl">Original works from artists worldwide — the artist is paid once your purchase is delivered.</p>

        <div className="flex flex-col md:flex-row gap-4 mt-8">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title or artist…"
              data-testid="gallery-search-input"
              className="w-full pl-11 pr-4 py-3 rounded-full border border-[var(--border-subtle)] text-sm bg-white focus:outline-none focus:border-[var(--gold)]" />
          </div>
          <select value={range} onChange={(e) => setRange(Number(e.target.value))}
            data-testid="gallery-price-filter"
            className="rounded-full border border-[var(--border-subtle)] px-5 py-3 text-sm bg-white focus:outline-none focus:border-[var(--gold)]">
            {RANGES.map((r, i) => <option key={r.label} value={i}>{r.label}</option>)}
          </select>
        </div>

        <div className="flex flex-wrap gap-2 mt-6">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCategory(c)} data-testid={`gallery-cat-${c.replace(/\s/g, "-").toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                category === c ? "bg-[var(--text-primary)] text-white" : "bg-white border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--gold)]"}`}>
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[var(--gold)]" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-24 text-[var(--text-muted)]" data-testid="gallery-empty">No works match your filters.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mt-10" data-testid="gallery-grid">
            {items.map((a) => <ArtworkCard key={a.id} art={a} />)}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
